import React, { useState } from 'react';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Database, Key, Eye } from 'lucide-react';

/**
 * Get Value Configuration Component
 * 
 * Simple UI for retrieving values from the state store.
 * Designed to be understandable by a 10-year-old.
 */
const GetValueConfig = ({ config, onChange }) => {
  const [key, setKey] = useState(config?.key || '');
  const [scope, setScope] = useState(config?.scope || 'user');
  const [defaultValue, setDefaultValue] = useState(config?.defaultValue || '');
  const [variableName, setVariableName] = useState(config?.variableName || 'state_value');

  const updateConfig = (updates) => {
    onChange({
      ...config,
      ...updates
    });
  };

  const scopes = [
    { value: 'user', label: 'My Data', description: 'Only you can access this' },
    { value: 'workspace', label: 'Team Data', description: 'Everyone in your workspace can access' },
    { value: 'execution', label: 'This Run Only', description: 'Temporary data for this workflow run' },
    { value: 'global', label: 'Global Data', description: 'Shared across everyone (admin only)' }
  ];

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Database className="w-5 h-5 text-blue-500" />
          <h3 className="text-lg font-semibold">Get Saved Value</h3>
        </div>
        <Badge variant="outline" className="bg-blue-500/10 text-blue-600 border-blue-500/30">
          Retrieve Data
        </Badge>
      </div>

      {/* Explanation */}
      <Card className="bg-blue-500/5 border-blue-500/20">
        <CardContent className="pt-4">
          <p className="text-sm text-muted-foreground">
            <strong>What does this do?</strong> This gets a value that you saved earlier. 
            Think of it like opening a box to see what's inside.
          </p>
        </CardContent>
      </Card>

      {/* Key Input */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Key className="w-4 h-4 text-blue-500" />
            <CardTitle className="text-sm font-medium">What's the name of the value?</CardTitle>
          </div>
        </CardHeader>
        <CardContent className="space-y-2">
          <Input
            placeholder="e.g., customer_count, cart_items, user_preferences"
            value={key}
            onChange={(e) => {
              setKey(e.target.value);
              updateConfig({ key: e.target.value });
            }}
            className="font-mono text-sm"
          />
          <p className="text-xs text-muted-foreground">
            This is like the label on the box. Use a name that describes what's inside.
          </p>
        </CardContent>
      </Card>

      {/* Scope Selector */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Where is it stored?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <Select value={scope} onValueChange={(value) => {
            setScope(value);
            updateConfig({ scope: value });
          }}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {scopes.map((s) => (
                <SelectItem key={s.value} value={s.value}>
                  <div className="flex flex-col">
                    <span className="font-medium">{s.label}</span>
                    <span className="text-xs text-muted-foreground">{s.description}</span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="p-3 bg-muted rounded-lg">
            <p className="text-xs">
              <strong>Selected:</strong> {scopes.find(s => s.value === scope)?.label}
              <br />
              {scopes.find(s => s.value === scope)?.description}
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Default Value */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">What if it doesn't exist?</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Input
            placeholder="e.g., 0, [], empty string"
            value={defaultValue}
            onChange={(e) => {
              setDefaultValue(e.target.value);
              updateConfig({ defaultValue: e.target.value });
            }}
          />
          <p className="text-xs text-muted-foreground">
            If the value hasn't been saved yet, use this instead. Leave empty for nothing.
          </p>
        </CardContent>
      </Card>

      {/* Variable Name */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Save it as (variable name)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          <Input
            placeholder="e.g., count, items, preferences"
            value={variableName}
            onChange={(e) => {
              setVariableName(e.target.value);
              updateConfig({ variableName: e.target.value });
            }}
            className="font-mono text-sm"
          />
          <p className="text-xs text-muted-foreground">
            After getting the value, you can use it in later steps with {`{{${variableName}}}`}
          </p>
        </CardContent>
      </Card>

      {/* Summary */}
      <Card className="bg-blue-500/5 border-blue-500/20">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-medium">Summary</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm">
          <p>
            <span className="text-muted-foreground">Get value:</span>{' '}
            <span className="font-mono text-xs bg-muted px-2 py-1 rounded">{key || '(not set)'}</span>
          </p>
          <p>
            <span className="text-muted-foreground">From:</span>{' '}
            <Badge variant="outline" className="bg-blue-500/10 text-blue-600">
              {scopes.find(s => s.value === scope)?.label}
            </Badge>
          </p>
          <p>
            <span className="text-muted-foreground">Save as:</span>{' '}
            <span className="font-mono text-xs">{`{{${variableName}}}`}</span>
          </p>
          {defaultValue && (
            <p>
              <span className="text-muted-foreground">Default:</span>{' '}
              <span className="font-mono text-xs">{defaultValue}</span>
            </p>
          )}
        </CardContent>
      </Card>

      {/* Example */}
      <Card className="bg-muted/50">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Eye className="w-4 h-4" />
            <CardTitle className="text-sm font-medium">Example</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-xs">
            <p className="text-muted-foreground">
              <strong>Scenario:</strong> You want to get the number of customers you've processed today.
            </p>
            <div className="p-2 bg-background rounded border">
              <p><strong>Key:</strong> customer_count_today</p>
              <p><strong>Scope:</strong> My Data (user)</p>
              <p><strong>Default:</strong> 0</p>
              <p><strong>Variable:</strong> count</p>
            </div>
            <p className="text-muted-foreground">
              Now you can use <code className="bg-muted px-1 rounded">{`{{count}}`}</code> in later steps!
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default GetValueConfig;
